<template>
    <div v-if="travelers.length" class="travelers">
        <edit-travelers-dialog
            v-if="isB2B && isOwnProduct && service.serviceType === 'ACCOMMODATION' && !authStore.isAccountant"
            :travelers="travelers"
            :service="service"
        />
        <div v-if="isMobile && isTravelersSliced && travelers?.length" class="text-caption">
            <span v-if="travelers[0]?.prefix" class="d-block traveler-label">
                {{ travelers[0].prefix }}
            </span>
            <span class="text-uppercase font-weight-bold" v-text="getTravelerName(travelers[0])" />
            <span v-if="travelers[0]?.citizenshipName" class="font-weight-medium text-medium-emphasis ml-1">
                {{ `(${travelers[0].citizenshipName})` }}
            </span>
            <span v-if="travelers[0]?.dateOfBirth" class="font-weight-bold ml-2">
                {{ $filters.dateShortFormat(travelers[0]?.dateOfBirth) }}
            </span>
        </div>
        <div
            v-for="(traveler, travelerIndex) in travelersSliced"
            :key="traveler.personId"
            class="travelers-item"
            :data-cy="`traveler-${travelerIndex}`"
        >
            <v-divider v-if="travelerIndex !== 0" class="my-3" />
            <v-row dense class="text-caption text-md-body-2">
                <v-col
                    v-if="traveler.prefix || traveler.name[0].firstName || traveler.name[0].lastName"
                    cols="12"
                    md="2"
                >
                    <span class="d-block traveler-label">{{ traveler.prefix || '&nbsp;' }}</span>
                    <span class="text-uppercase font-weight-bold" v-text="getTravelerName(traveler)" />
                    <span v-if="traveler.citizenshipName" class="font-weight-medium text-medium-emphasis ml-1">
                        {{ `(${traveler.citizenshipName})` }}
                    </span>
                    <div v-if="service.serviceDetails[0].seats && service.serviceDetails[0].seats[travelerIndex]">
                        {{
                            `${$t('flights.seat_number')}: ${service.serviceDetails[0].seats[travelerIndex].seatNumber}`
                        }}
                    </div>
                    <div
                        v-if="
                            isShowSeats &&
                            service?.modifyManualSeat?.length &&
                            service?.modifyManualSeat[travelerIndex]?.seatNumber
                        "
                    >
                        <span class="text-medium-emphasis">{{ $t('seat') }}:</span>
                        <span class="font-weight-bold ml-2">
                            {{ service?.modifyManualSeat[travelerIndex]?.seatNumber }}
                        </span>
                    </div>
                </v-col>
                <v-col v-else cols="12" md="2" class="d-flex align-center flex-column justify-center">
                    <div class="text-center text-caption text--secondary">
                        {{ $t('no_data') }}
                    </div>
                    <div v-if="service.serviceDetails[0].seats && service.serviceDetails[0].seats[travelerIndex]">
                        {{
                            `${$t('flights.seat_number')}: ${service.serviceDetails[0].seats[travelerIndex].seatNumber}`
                        }}
                    </div>
                </v-col>
                <slot name="mealType" :traveler="traveler" />
                <v-col v-if="getAgeType(traveler)" cols="12" md="2">
                    <span class="d-block traveler-label">{{ $t('type') }}</span>
                    <span class="font-weight-bold">
                        {{ getAgeType(traveler) }}
                    </span>
                </v-col>
                <v-col v-if="emptyAdditionalFields || traveler.dateOfBirth" cols="12" md="2">
                    <span class="d-block traveler-label">{{ $t('birthdate') }}</span>
                    <span class="font-weight-bold">
                        {{ traveler.dateOfBirth ? $filters.dateShortFormat(traveler.dateOfBirth) : '-' }}
                    </span>
                </v-col>
                <v-col v-if="traveler.email || traveler.phone" cols="12" md="2">
                    <span class="d-block traveler-label">{{ $t('contacts') }}</span>
                    <span v-if="traveler.email" class="d-block font-weight-bold">
                        {{ traveler.email }}
                    </span>
                    <span v-if="traveler.phone" class="d-block font-weight-bold">
                        {{ traveler.phone }}
                    </span>
                </v-col>
                <v-col v-if="traveler.personalNumber" cols="12" md="2">
                    <span class="d-block traveler-label">{{ $t('personal_number') }}</span>
                    <span class="font-weight-bold">
                        {{ traveler.personalNumber }}
                    </span>
                </v-col>
                <v-col v-if="emptyAdditionalFields || traveler.passports.length" cols="12" md="2">
                    <span class="d-block traveler-label">{{ $t('document') }}</span>
                    <span class="font-weight-bold">
                        {{ traveler.passports.length ? traveler.passports[0].number : '-' }}
                    </span>
                </v-col>
                <v-col
                    v-if="emptyAdditionalFields || (traveler.passports.length && traveler.passports[0].expiryDate)"
                    cols="12"
                    md="2"
                >
                    <span class="d-block traveler-label">{{ $t('expire_date') }}</span>
                    <span class="font-weight-bold">
                        {{
                            traveler.passports.length ? $filters.dateShortFormat(traveler.passports[0].expiryDate) : '-'
                        }}
                    </span>
                </v-col>
                <v-col v-if="traveler.knownTravelerNumber" cols="12" md="2">
                    <span class="d-block traveler-label">{{ $t('known_traveller_number') }}</span>
                    <span class="font-weight-bold">
                        {{ traveler.knownTravelerNumber }}
                    </span>
                </v-col>
                <v-col v-if="traveler.bonusCards && traveler.bonusCards.length" cols="12" md="2">
                    <span class="d-block traveler-label">{{ $t('bonus_card') }}</span>
                    <span class="font-weight-bold">
                        {{ traveler.bonusCards.map(({number}) => number).join(', ') }}
                    </span>
                </v-col>
                <v-col
                    v-if="
                        traveler.preferences &&
                        (traveler.preferences.mealTypeName || traveler.preferences.seatAllocationName)
                    "
                    cols="12"
                    md="2"
                >
                    <span class="d-block traveler-label">{{ $t('preferences') }}</span>
                    <div v-if="traveler.preferences.mealTypeName" class="font-weight-bold">
                        {{ traveler.preferences.mealTypeName }}
                    </div>
                    <div v-if="traveler.preferences.seatAllocationName">
                        {{ $t('flights.seat_allocation') }}:
                        <span class="font-weight-bold">{{ traveler.preferences.seatAllocationName }}</span>
                    </div>
                </v-col>
                <v-col v-if="flightSegments(travelerIndex).length" cols="12" class="py-0">
                    <v-data-table
                        :headers="headers(flightSegments(travelerIndex))"
                        :items="flightSegments(travelerIndex)"
                        :items-per-page="-1"
                        density="compact"
                        hide-default-footer
                        class="text-body-2 d-none d-md-block flight-table"
                        :class="{'pt-2 pt-md-5': headers(flightSegments(travelerIndex))?.length}"
                    >
                        <template v-slot:item.segment="{item}">
                            <p v-if="item.segment.flightSegmentName || item.segment.aircraftName">
                                {{ item.segment.flightSegmentName }}
                                {{ item.segment.aircraftName ? `, ${item.segment.aircraftName}` : '' }}
                            </p>
                        </template>
                        <template v-slot:item.seat="{item}">
                            <div v-if="item.seat?.rowNumber || item.seat?.seatNumber" class="d-flex flex-nowrap gc-2">
                                <strong>{{ item.seat.rowNumber }}{{ item.seat.seatNumber }}</strong>
                                <span :class="flightServiceStatusColor(item.seat.bookingStatus)">
                                    {{ getSeatBookingStatus(item) }}
                                </span>
                            </div>
                        </template>
                        <template v-slot:item.mealIncluded="{item}">
                            <template v-if="item.mealIncluded !== undefined">
                                <v-icon v-if="item.mealIncluded" size="small" start color="success">
                                    mdi-check-circle
                                </v-icon>
                                <v-icon v-else-if="item.mealIncluded === false" size="small" start color="error">
                                    mdi-close-circle
                                </v-icon>
                            </template>
                        </template>
                        <template v-slot:item.baggage="{item}">
                            <template v-if="item.baggage.length">
                                <p v-for="(service, serviceIndex) in item.baggage" :key="serviceIndex" class="mr-1">
                                    <span>
                                        {{ service.name }} {{ service.quantity }}
                                        {{ $t('flights.baggage_item', service.quantity) }}
                                    </span>
                                    <span v-if="service.status" :class="flightServiceStatusColor(service.status)">
                                        {{ $t(`flights.flightExtraServiceStatus.${service.status}`) }}
                                    </span>
                                </p>
                            </template>
                        </template>
                    </v-data-table>
                    <div
                        v-for="(item, index) in flightSegments(travelerIndex)"
                        :key="index"
                        class="text-caption d-block d-md-none"
                    >
                        <template v-if="item.segment.flightSegmentName || item.segment.aircraftName">
                            <p class="text-medium-emphasis">{{ $t('productType.flight') }}</p>
                            <p class="pb-2">
                                {{ item.segment.flightSegmentName }}
                                {{ item.segment.aircraftName ? `, ${item.segment.aircraftName}` : '' }}
                            </p>
                        </template>
                        <template v-if="item.seat?.rowNumber || item.seat?.seatNumber">
                            <p class="text-medium-emphasis text-capitalize">{{ $t('flights.seat', 1) }}</p>
                            <div class="pb-2 d-flex flex-nowrap gc-2">
                                <strong>{{ item.seat.rowNumber }}{{ item.seat.seatNumber }}</strong>
                                <span :class="flightServiceStatusColor(item.seat.bookingStatus)">
                                    {{ getSeatBookingStatus(item) }}
                                </span>
                            </div>
                        </template>
                        <div v-if="item.mealIncluded !== undefined" class="d-flex align-center gc-2 pb-2">
                            <span class="text-medium-emphasis">{{ $t('meal_included') }}</span>
                            <v-icon v-if="item.mealIncluded" size="small" start color="success">
                                mdi-check-circle
                            </v-icon>
                            <v-icon v-else-if="item.mealIncluded === false" size="small" start color="error">
                                mdi-close-circle
                            </v-icon>
                        </div>
                        <template v-if="item.baggage.length">
                            <p class="text-medium-emphasis">{{ $t('flights.baggage') }}</p>
                            <div class="pb-2">
                                <p v-for="(service, serviceIndex) in item.baggage" :key="serviceIndex">
                                    <span class="mr-1">
                                        {{ service.name }} {{ service.quantity }}
                                        {{ $t('flights.baggage_item', service.quantity) }}
                                    </span>
                                    <span v-if="service.status" :class="flightServiceStatusColor(service.status)">
                                        {{ $t(`flights.flightExtraServiceStatus.${service.status}`) }}
                                    </span>
                                </p>
                            </div>
                        </template>
                    </div>
                </v-col>
                <v-col v-if="traveler.onExtraBed || traveler.onWithoutPlace" cols="12" md="2">
                    <div v-if="traveler.onExtraBed" class="text-medium-emphasis">
                        <v-icon size="small" color="primary" start>mdi-bed-outline</v-icon>
                        {{ $t('extra_bed') }}
                    </div>
                    <div v-if="traveler.onWithoutPlace" class="text-medium-emphasis">
                        <v-icon size="small" color="warning" start>mdi-bed-empty</v-icon>
                        {{ $t('without_bed') }}
                    </div>
                </v-col>
            </v-row>
        </div>
        <v-btn
            v-if="travelers.length > travelersToShowNumber"
            block
            size="small"
            variant="flat"
            class="my-2 text-none"
            data-cy="show-all"
            @click="isTravelersSliced = !isTravelersSliced"
        >
            <span class="text-primary" v-text="`${isTravelersSliced ? $t('show') : $t('hide')} ${$t('all')}`" />
        </v-btn>
        <v-row v-if="quantityOfTravelersDifference && quantityOfTravelersDifference > 0">
            <v-col cols="12">
                <span class="text--secondary" v-text="`+ ${quantityOfTravelersDifference} ${$t('order_travelers')}`" />
            </v-col>
        </v-row>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import TouristPassportForm from '~src/components/booking/forms/touristPassportForm.src'
    import EditTravelersDialog from '@/components/account/orderServices/EditTravelersDialog.vue'

    @Component({
        components: {EditTravelersDialog, TouristPassportForm},
    })
    export class Travelers extends Vue {
        @Prop({required: true}) travelers
        @Prop() product
        @Prop() orderEndDate
        @Prop({required: true}) service
        @Prop({default: false, type: Boolean}) emptyAdditionalFields
        @Prop({default: () => []}) details

        isTravelersSliced = true
        travelersToShowNumber = 4

        created() {
            if (this.isMobile) this.travelersToShowNumber = 0
        }

        getTravelerName(traveler) {
            return `${traveler.name[0].firstName || ''} ${traveler.name[0].lastName || ''}`
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get isShowSeats() {
            const isServiceTypeValid = serviceType => {
                return serviceType === 'TRANSFER' || serviceType === 'ACTIVITY'
            }
            const isExtraServiceTypeValid = extraServiceProductType => {
                return extraServiceProductType === 'TRANSFER' || extraServiceProductType === 'EXCURSION'
            }
            const isExtraServiceValid = service => {
                return (
                    service?.serviceType === 'EXTRASERVICE' &&
                    isExtraServiceTypeValid(service?.serviceDetails[0]?.extraServiceProductType)
                )
            }

            return (
                (this.service?.seatsVisibleForClient || this.isTO1) &&
                (isServiceTypeValid(this.service?.serviceType) || isExtraServiceValid(this.service))
            )
        }

        headers(segments) {
            let columns = []
            if (segments.some(item => item.segment?.flightSegmentName || item.segment?.aircraftName)) {
                columns.push({
                    title: this.$t('productType.flight'),
                    value: 'segment',
                    width: '30%',
                    minWidth: 250,
                })
            }
            if (segments.some(item => item.seat?.rowNumber || item.seat?.seatNumber)) {
                columns.push({title: this.$t('seat'), value: 'seat', width: '20%', minWidth: 150})
            }
            if (segments.some(item => item.mealIncluded)) {
                columns.push({title: this.$t('meal_included'), value: 'mealIncluded', width: 100, minWidth: 100})
            }
            if (segments.some(item => item.baggage.length)) {
                columns.push({
                    title: this.$t('flights.baggage'),
                    value: 'baggage',
                    width: '30%',
                    minWidth: 200,
                })
            }
            if (columns.length > 1) columns.push({title: '', value: 'empty'})

            return columns
        }

        get isTO1() {
            return authStore.isTO1
        }

        getAgeType() {
            return null
        }

        getAge(dateOfBirth) {
            return this.$dateFns.differenceInYears(new Date(this.orderEndDate), this.$dateFns.parseISO(dateOfBirth))
        }

        flightSegments(travelerIndex) {
            if (
                this.product === 'EXTRASERVICE' &&
                this.service.serviceDetails[0]?.extraServiceProductType === 'FLIGHT'
            ) {
                return (
                    this.service.serviceDetails[0]?.segments?.map(s => {
                        return {
                            segment: s,
                            seat: s.seats.find(seat => +seat.travelerRPH === +travelerIndex),
                            baggage: [],
                            mealIncluded: s.mealIncluded,
                        }
                    }) || []
                )
            }

            if (this.product !== 'FLIGHT' || !this.details || !this.details[0]) return []
            const flightBaggage = this.details[0].flightExtraServices.filter(
                flightExtraService =>
                    flightExtraService.type === 'BAGGAGE' && flightExtraService.travelerRPH - 1 === travelerIndex
            )
            return this.details[0].itineraries.reduce((segments, itinerary, itineraryIndex) => {
                itinerary.flightSegments.forEach((segment, segmentIndex) => {
                    const seat = segment.seats
                        ? segment.seats.find(seat => seat.travelerRPH - 1 === travelerIndex)
                        : null
                    const baggage = flightBaggage.filter(flightBaggage =>
                        flightBaggage.segmentRphs.includes(`${itineraryIndex + 1}${segmentIndex + 1}`)
                    )
                    if (seat || baggage.length) {
                        segments.push({
                            segment,
                            seat,
                            baggage,
                        })
                    }
                })
                return segments
            }, [])
        }

        getSeatBookingStatus(item) {
            return item.seat.bookingStatus === 'Unable to Sale – Have Waitlisted'
                ? 'Cancelled'
                : item.seat.bookingStatus === 'Placing on waitlist/new schedule'
                  ? 'Pending confirmation (not guaranteed)'
                  : item.seat.bookingStatus
        }

        flightServiceStatusColor(status) {
            const statusUppercase = status?.split(' ').join('_').toUpperCase()
            if (
                statusUppercase === 'REJECTED' ||
                statusUppercase === 'CANCELLED' ||
                statusUppercase === 'HAVE_SOLD' ||
                statusUppercase === 'DECLINED' ||
                statusUppercase === 'UNABLE_TO_SELL' ||
                statusUppercase === 'UNABLE_TO_SALE_–_HAVE_WAITLISTED'
            ) {
                return 'text-error'
            } else if (
                status === 'Placing on waitlist/new schedule' ||
                statusUppercase === 'PENDING_CONFIRMATION' ||
                statusUppercase === 'CONFIRMATION_PENDING' ||
                statusUppercase === 'PLACING_TO_WAITLIST' ||
                statusUppercase === 'PLACING_ON_WAITLIST' ||
                statusUppercase === 'PENDING_CONFIRMATION_(NOT_GUARANTEED)'
            ) {
                return 'text-warning'
            } else {
                return 'text-success'
            }
        }

        get isB2B() {
            return authStore.isB2B
        }

        get isOwnProduct() {
            return runtimeStore.ownProduct(this.service.supplierCode)
        }

        get travelersSliced() {
            return this.isTravelersSliced ? this.travelers.slice(0, this.travelersToShowNumber) : this.travelers
        }

        get quantityOfTravelers() {
            return this.service?.serviceDetails[0]?.quantityOfTravelers
        }

        get quantityOfTravelersDifference() {
            if (this.quantityOfTravelers && this.quantityOfTravelers > this.travelers.length) {
                return this.quantityOfTravelers - this.travelers.length
            }

            return null
        }
    }

    export default Travelers
</script>

<style lang="scss" scoped>
    .flight-table {
        :deep(td),
        :deep(th) {
            border: none !important;
        }
        :deep(th) {
            height: 20px !important;
            color: rgba(var(--v-theme-on-background), var(--v-medium-emphasis-opacity)) !important;
            font-weight: 400 !important;
        }
        :deep(td:first-of-type),
        :deep(th:first-of-type) {
            padding-left: 0 !important;
        }
    }
    .travelers {
        position: relative;
    }

    .traveler-label,
    .title {
        line-height: 1;
    }
</style>
